import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import './About.css';

/** Auto-advance interval (progress bar duration matches this). */
const SLIDE_DURATION_MS = 8000;

const ABOUT_SLIDES = [
  {
    title: 'Secure Cloud Innovation',
    body:
      "Backed by cutting-edge technology and a team of industry experts, we're building an innovative solution that will help organizations move quickly in the cloud while not compromising on security & operational efficiency."
  },
  {
    title: 'AI and Agentic native',
    body:
      'As a fast-growing startup, we thrive on agility, collaboration, and a passion for solving complex security challenges. Joining us means your contributions will have a direct impact on shaping the future of cybersecurity.'
  }
];

const About = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  /** Remounts the active segment’s fill animation when the slide changes. */
  const [progressKey, setProgressKey] = useState(0);
  const regionRef = useRef(null);
  const prevIndexRef = useRef(0);
  const headingId = useId();
  const slideHeadingId = useId();

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setPrefersReducedMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((i) => (i + 1) % ABOUT_SLIDES.length);
  }, []);

  const goPrev = useCallback(() => {
    setActiveIndex((i) => (i - 1 + ABOUT_SLIDES.length) % ABOUT_SLIDES.length);
  }, []);

  /* Advance slide on a fixed interval when autoplay is allowed */
  useEffect(() => {
    if (prefersReducedMotion) return undefined;
    const id = window.setTimeout(() => {
      goNext();
    }, SLIDE_DURATION_MS);
    return () => window.clearTimeout(id);
  }, [activeIndex, prefersReducedMotion, goNext]);

  useEffect(() => {
    if (prevIndexRef.current !== activeIndex) {
      setProgressKey((k) => k + 1);
      prevIndexRef.current = activeIndex;
    }
  }, [activeIndex]);

  useEffect(() => {
    const el = regionRef.current;
    if (!el) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    };

    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
  }, [goNext, goPrev]);

  const active = ABOUT_SLIDES[activeIndex];

  return (
    <section id="about" className="about" aria-labelledby={headingId}>
      <div className="page-container about-inner">
        <header className="page-section__header about-header">
          <p className="page-section__eyebrow about-eyebrow">About us</p>
          <h2 id={headingId} className="about-title">
            The Circumvent Mission
          </h2>
          <p className="about-lede">
            We are on a mission to simplify how cloud and security teams identify, prioritize and remediate
            misconfiguration and vulnerabilities in their cloud applications and infrastructure.
          </p>
        </header>

        <div
          ref={regionRef}
          className="about-carousel"
          role="region"
          aria-roledescription="carousel"
          aria-label="About Circumvent. Slides advance automatically."
          tabIndex={0}
        >
          <div className="about-carousel__frame">
            {/* Transparent stack layer; section background shows through. Cube art is rounded in CSS. */}
            <div className="about-carousel__base" aria-hidden="true" />
            <div className="about-carousel__cubes" aria-hidden="true">
              <div className="about-carousel__cubes-clip">
                <img
                  className="about-carousel__cubes-img"
                  src="/images/about-cubes.png"
                  alt=""
                  decoding="async"
                  loading="lazy"
                />
                <div className="about-carousel__cubes-scrim" aria-hidden="true" />
              </div>
            </div>
            <div className="about-carousel__left">
              <article
                className="about-carousel__card"
                aria-labelledby={slideHeadingId}
                aria-live="polite"
              >
                <div key={activeIndex} className="about-carousel__card-inner">
                  <h3 id={slideHeadingId} className="about-carousel__card-title">
                    {active.title}
                  </h3>
                  <p className="about-carousel__card-body">{active.body}</p>
                </div>
              </article>

              <nav className="about-carousel__segments" aria-label="Slides">
                <ul className="about-carousel__segments-list">
                  {ABOUT_SLIDES.map((slide, index) => {
                    const isPast = index < activeIndex;
                    const isActive = index === activeIndex;
                    return (
                      <li key={slide.title} className="about-carousel__segments-item">
                        <button
                          type="button"
                          className="about-carousel__segment"
                          aria-label={`${slide.title}. Slide ${index + 1} of ${ABOUT_SLIDES.length}`}
                          aria-current={isActive ? 'true' : undefined}
                          onClick={() => setActiveIndex(index)}
                        >
                          <span className="about-carousel__segment-track">
                            {isPast && (
                              <span
                                className="about-carousel__segment-fill about-carousel__segment-fill--complete"
                                aria-hidden="true"
                              />
                            )}
                            {isActive && !prefersReducedMotion && (
                              <span
                                key={progressKey}
                                className="about-carousel__segment-fill"
                                style={{ '--about-slide-ms': `${SLIDE_DURATION_MS}ms` }}
                                aria-hidden="true"
                              />
                            )}
                            {isActive && prefersReducedMotion && (
                              <span
                                className="about-carousel__segment-fill about-carousel__segment-fill--complete"
                                aria-hidden="true"
                              />
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
